import { useState, useCallback, useEffect, useMemo } from 'react';
import Typography from '@mui/material/Typography';
import { Alert, CircularProgress, Box } from '@mui/material';
import AuthenticationPage from './AuthenticationPage';
import SignInForm from './SignInForm';
import AccountPicker from './AccountPicker';
import OrgSelectionStep from './OrgSelectionStep';
import useCognito from '../hooks/useCognito';
import cognitoStorageAdapter from '../storage/cognitoStorageAdapter';
import { cognitoClientId } from '@/shared/constants';
import {
  getKnownAccounts,
  removeAccount,
  hasRememberedSession,
  getCognitoUsername,
  addOrUpdateAccount,
} from '../storage/knownAccountsRegistry';
import getCurrentUserQuery from '@/shared/hooks/user/getCurrentUser';
import useUpdateSession from '@/shared/hooks/user/useUpdateSession';
import { getReturnUrl, returnPicksOwnOrg } from '../functions/returnUrl';
import { Org } from '@/types/user';

type ViewState = 'picker' | 'sign-in' | 'auto-login' | 'org-selection';

const LAST_AUTH_USER_KEY = `CognitoIdentityServiceProvider.${cognitoClientId}.LastAuthUser`;

const SignInPage = () => {
  const accounts = getKnownAccounts();
  const [view, setView] = useState<ViewState>(accounts.length > 0 ? 'picker' : 'sign-in');
  const [prefillEmail, setPrefillEmail] = useState<string | undefined>();
  const [autoLoginError, setAutoLoginError] = useState<string | undefined>();
  const [knownAccounts, setKnownAccounts] = useState(accounts);

  // org-selection state (used when auto-login user belongs to multiple orgs)
  const [pendingOrgs, setPendingOrgs] = useState<Org[]>([]);
  const [pendingEmail, setPendingEmail] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [orgSelectionLoading, setOrgSelectionLoading] = useState(false);
  const [orgSelectionErrors, setOrgSelectionErrors] = useState<string[]>([]);

  const cognito = useCognito();
  const { data: { data } = {}, refetch: refetchUser } = getCurrentUserQuery();
  const updateSession = useUpdateSession();
  const currentUser = useMemo(() => data?.user, [data?.user]);

  const refreshAccounts = useCallback(() => {
    setKnownAccounts(getKnownAccounts());
  }, []);

  // After auto-login: once Cognito session is restored and user data arrives,
  // route to org-selection (multi-org) or auto-select and proceed (single org).
  useEffect(() => {
    if (view !== 'auto-login' || !cognito.authenticated || !currentUser) return;

    const orgs: Org[] = currentUser.orgs ?? [];

    if (orgs.length === 0) {
      setAutoLoginError('No organizations available for this account.');
      setView('sign-in');
      return;
    }

    const returnUrl = getReturnUrl(window.location.search, window.location.origin);

    if (orgs.length === 1) {
      updateSession
        .mutateAsync({ org: orgs[0].id })
        .then(() => {
          addOrUpdateAccount({
            email: pendingEmail,
            cognitoUsername: cognito.user?.getUsername(),
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
            orgName: orgs[0].name,
            remembered: true,
            lastLoginAt: Date.now(),
          });
          window.location.href = returnUrl;
        })
        .catch(() => {
          setAutoLoginError('Failed to initialize session. Please sign in again.');
          setView('sign-in');
        });
      return;
    }

    // A page that asks for the org itself (MCP consent) gets no org step, and no
    // PUT /session: that records the org as the user's own choice of default.
    if (returnPicksOwnOrg(returnUrl)) {
      addOrUpdateAccount({
        email: pendingEmail,
        cognitoUsername: cognito.user?.getUsername(),
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        orgName: data?.org?.name,
        remembered: true,
        lastLoginAt: Date.now(),
      });
      window.location.href = returnUrl;
      return;
    }

    // Multiple orgs — let the user choose
    setPendingOrgs(orgs);
    setView('org-selection');
  }, [view, cognito.authenticated, currentUser]);

  const handleAutoLoginOrgSelect = useCallback(async () => {
    const org = pendingOrgs.find((o) => o.id === selectedOrgId);
    if (!org) {
      setOrgSelectionErrors(['Please select an organization.']);
      return;
    }

    setOrgSelectionLoading(true);
    try {
      await updateSession.mutateAsync({ org: org.id });
      addOrUpdateAccount({
        email: pendingEmail,
        cognitoUsername: cognito.user?.getUsername(),
        firstName: currentUser?.firstName,
        lastName: currentUser?.lastName,
        orgName: org.name,
        remembered: true,
        lastLoginAt: Date.now(),
      });
      window.location.href = getReturnUrl(window.location.search, window.location.origin);
    } catch {
      setOrgSelectionErrors(['Failed to select organization. Please try again.']);
      setOrgSelectionLoading(false);
    }
  }, [pendingOrgs, selectedOrgId, pendingEmail, cognito.user, currentUser, updateSession]);

  const handleSelectAccount = useCallback(
    (email: string) => {
      console.debug('[SignInPage] Account selected:', email);

      if (!hasRememberedSession(email)) {
        console.debug('[SignInPage] No refresh token found locally — falling back to password');
        setPrefillEmail(email);
        setAutoLoginError('Session expired. Please sign in again.');
        setView('sign-in');
        return;
      }

      // Use the Cognito username (UUID) if available, since the SDK stores tokens under it
      const cognitoUsername = getCognitoUsername(email);
      const authUser = cognitoUsername || email;
      console.debug('[SignInPage] Refresh token found — attempting auto-login | authUser:', authUser);

      setView('auto-login');
      setAutoLoginError(undefined);
      setPendingEmail(email);

      cognitoStorageAdapter.setRememberMe(true);
      localStorage.setItem(LAST_AUTH_USER_KEY, authUser);

      cognito.getSession({
        onSuccess: () => {
          console.debug('[SignInPage] Auto-login succeeded — fetching user to check orgs');
          refetchUser();
        },
        onFailure: () => {
          console.warn('[SignInPage] Auto-login failed — refresh token likely expired');
          setPrefillEmail(email);
          setAutoLoginError('Session expired. Please sign in again.');
          setView('sign-in');
        },
      });
    },
    [cognito],
  );

  const handleUseAnother = useCallback(() => {
    setPrefillEmail(undefined);
    setAutoLoginError(undefined);
    setView('sign-in');
  }, []);

  const handleRemoveAccount = useCallback(
    (email: string) => {
      removeAccount(email);
      const updated = getKnownAccounts();
      setKnownAccounts(updated);
      if (updated.length === 0) {
        setView('sign-in');
      }
    },
    [],
  );

  const handleBackToPicker = useCallback(() => {
    refreshAccounts();
    setAutoLoginError(undefined);
    setView('picker');
  }, [refreshAccounts]);

  return (
    <AuthenticationPage>
      {view === 'auto-login' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
          <CircularProgress size={40} sx={{ color: 'white' }} />
          <Typography variant="body2" sx={{ color: 'grey.400' }}>
            Restoring session...
          </Typography>
        </Box>
      )}

      {view === 'picker' && (
        <AccountPicker
          accounts={knownAccounts}
          onSelectAccount={handleSelectAccount}
          onUseAnother={handleUseAnother}
          onRemoveAccount={handleRemoveAccount}
        />
      )}

      {view === 'org-selection' && (
        <>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Select Organization
          </Typography>
          <OrgSelectionStep
            organizations={pendingOrgs}
            selectedOrgId={selectedOrgId}
            loading={orgSelectionLoading}
            errors={orgSelectionErrors}
            onOrgChange={(id) => setSelectedOrgId(id)}
            onContinue={handleAutoLoginOrgSelect}
            onLogOut={() =>
              cognito.signOut({
                onSuccess: () => setView('sign-in'),
                onFailure: () => setView('sign-in'),
              })
            }
          />
        </>
      )}

      {view === 'sign-in' && (
        <>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Log In
          </Typography>

          {autoLoginError && (
            <Alert severity="warning" sx={{ mb: 2, width: '100%' }}>
              {autoLoginError}
            </Alert>
          )}

          <SignInForm
            prefillEmail={prefillEmail}
            onBack={knownAccounts.length > 0 ? handleBackToPicker : undefined}
          />
        </>
      )}
    </AuthenticationPage>
  );
};

export default SignInPage;
